import { useState } from "react";
import { useEventStore, useGuestStore } from "store";
import * as SpacesService from "libs/digitalocean/SpacesService";
import CdtAppPublicApiClient from "cdt-app-public-api/dist";
import { useLogEvent } from "hooks/useLogEvent";
import { NewPostFeedEvent } from "cdt-app-public-api/dist/types";
import { clarity } from "react-microsoft-clarity";

interface CreatePostData {
  id?: string;
  postId?: string;
  eventId: string;
  eventCode: string;
  postText?: string | null;
  postImageUrl?: string | null;
  postImageStoragePath?: string | null;
  thumbnailUrl?: string | null;
  thumbnailStoragePath?: string | null;
  guestId: string;
  guestName: string | null;
  guestPhotoUrl?: string | null;
  guestPhotoStoragePath?: string | null;
}

export const useCreatePost = () => {
  const { guest } = useGuestStore();
  const { event } = useEventStore();
  const [newPostText, setNewPostText] = useState<string>("");
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [croppedImage, setCroppedImage] = useState<File | null>(null);
  const [tempImageFile, setTempImageFile] = useState<File | null>(null);
  const [showImageResizer, setShowImageResizer] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [creationProgress, setCreationProgress] = useState(0);

  const { logStartUploadFile, logFinishUploadFile, logUploadFileError } =
    useLogEvent();

  const uploadOriginalFile = async (
    file: File,
    onProgress: (progress: number) => void,
    gameTag?: string
  ): Promise<string> => {
    const randomCode = Math.random().toString(36).substring(2, 8);
    const now = new Date().toISOString();
    const baseFileName = `feed-post-${event?.title}-[${event?.code}]-[${now}]_${randomCode}`;
    const fileExt = file.name.split(".").pop() as string;
    const fileName = `${baseFileName}.${fileExt}`;
    const fileStoragePath = `feed-posts/${event?.id}_${event?.code}/${fileName}`;

    logStartUploadFile({ fileName });

    try {
      await SpacesService.uploadFile(file, fileStoragePath, onProgress);

      const tags = ["feed-post", "release-now"];
      if (gameTag) {
        tags.push(gameTag);
      }

      const uploadData = {
        eventId: event?.id as string,
        eventCode: event?.code as string,
        fileStoragePath,
        fileName,
        fileSize: file.size,
        fileType: file.type,
        fileExt,
        guestId: guest?.id,
        guestName: guest?.name,
        guestPhotoStoragePath: guest?.photoStoragePath as string,
        tags,
      };

      const cdtAppPublicApiClient = new CdtAppPublicApiClient();
      await cdtAppPublicApiClient.createUpload(uploadData);

      logFinishUploadFile({
        fileName,
        fileId: crypto.randomUUID(),
      });

      return fileStoragePath;
    } catch (error) {
      const errorMsg = `uploadPostImage [${JSON.stringify(error)}]`;
      logUploadFileError({ error: errorMsg });
      throw new Error(errorMsg);
    }
  };

  const uploadThumbnailDirectly = async (
    file: File,
    originalStoragePath: string,
    onProgress: (progress: number) => void
  ): Promise<string> => {
    const pathParts = originalStoragePath.split(".");
    const extension = pathParts.pop();
    const basePathWithoutExt = pathParts.join(".");
    const thumbnailStoragePath = `${basePathWithoutExt}_feed.${extension}`;

    try {
      await SpacesService.uploadFile(file, thumbnailStoragePath, onProgress);
      return thumbnailStoragePath;
    } catch (error) {
      const errorMsg = `uploadThumbnail [${JSON.stringify(error)}]`;
      logUploadFileError({ error: errorMsg });
      throw new Error(errorMsg);
    }
  };

  const savePostToDatabase = async (
    postData: CreatePostData
  ): Promise<void> => {
    try {
      const cdtAppPublicApiClient = new CdtAppPublicApiClient();

      await cdtAppPublicApiClient.createFeedPost(postData);
    } catch (error) {
      throw new Error(`Failed to save post: ${error}`);
    }
  };

  const submitNewPost = async (
    guestPhotoUrl: string | null,
    guestName?: string | null,
    onSuccess?: (post: NewPostFeedEvent) => void,
    gameTag?: string
  ) => {
    if (!newPostText.trim() && !selectedImage) {
      throw new Error("Post text or image is required.");
    }

    if (!event) {
      throw new Error("Event is not set.");
    }

    setIsCreating(true);
    setCreationProgress(0);

    try {
      let postImageUrl = "";
      let postImageStoragePath = null;
      let thumbnailUrl = "";
      let thumbnailStoragePath = null;

      if (selectedImage) {
        setCreationProgress(10);

        postImageStoragePath = await uploadOriginalFile(
          selectedImage,
          (progress) => {
            const adjustedProgress = 10 + progress * 0.4;
            setCreationProgress(adjustedProgress);
          },
          gameTag
        );
        setCreationProgress(50);

        const thumbnailFile = croppedImage || selectedImage;
        thumbnailStoragePath = await uploadThumbnailDirectly(
          thumbnailFile,
          postImageStoragePath,
          (progress) => {
            const adjustedProgress = 50 + progress * 0.2;
            setCreationProgress(adjustedProgress);
          }
        );

        try {
          postImageUrl = await SpacesService.getFileUrl(postImageStoragePath);
          thumbnailUrl = await SpacesService.getFileUrl(thumbnailStoragePath);
        } catch (error) {
          console.error("Erro ao converter storage path em URL:", error);
        }
      }

      setCreationProgress(70);
      if (!event.id || !event.code || !guest?.id) {
        throw new Error("Event ID, code, or guest ID is missing.");
      }

      const postId = crypto.randomUUID();
      const newPost: NewPostFeedEvent = {
        type: "new-post",
        id: postId,
        eventId: event.id,
        eventCode: event.code,
        guestId: guest?.id,
        guestName: guestName || guest?.name || null,
        guestPhotoStoragePath: guest?.photoStoragePath || null,
        guestPhotoUrl,
        createdAt: new Date().toISOString(),
        likes: [],
        totalLikes: 0,
        postId,
        postText: newPostText.trim() || null,
        postImageStoragePath,
        postImageUrl: thumbnailUrl || postImageUrl,
        thumbnailStoragePath,
        thumbnailUrl,
      };

      setCreationProgress(80);

      await savePostToDatabase({
        id: postId,
        postId,
        eventId: event.id,
        eventCode: event.code,
        postText: newPostText.trim() || null,
        postImageUrl,
        postImageStoragePath,
        thumbnailUrl,
        thumbnailStoragePath,
        guestId: guest?.id || "anonymous",
        guestName: guestName || guest?.name || null,
        guestPhotoStoragePath: guest?.photoStoragePath || null,
      });

      setCreationProgress(100);

      if (clarity.hasStarted()) {
        clarity.setTag("feed_post", "created");
        clarity.setTag("engagement_level_creator", "true");
      }

      onSuccess?.(newPost);
      resetForm();
      return newPost;
    } catch (error) {
      console.error("Erro ao criar post:", error);
      throw error;
    } finally {
      setIsCreating(false);
      setCreationProgress(0);
    }
  };

  const resetForm = () => {
    setNewPostText("");
    setSelectedImage(null);
    setCroppedImage(null);
    setTempImageFile(null);
    setShowImageResizer(false);
    setCreationProgress(0);
  };

  return {
    newPostText,
    setNewPostText,
    selectedImage,
    setSelectedImage,
    croppedImage,
    setCroppedImage,
    tempImageFile,
    setTempImageFile,
    showImageResizer,
    setShowImageResizer,
    isCreating,
    creationProgress,
    submitNewPost,
    resetForm,
  };
};
